import PopoverPosition from '../helpers/popover-position.js'

// Vue directive for Bootstrap-compatible popovers without jQuery
// Uses same HTML structure and CSS as Bootstrap 3

const instances = []
let uid = 0

// Hide all popovers on touch outside (for mobile)
document.addEventListener('touchstart', e => {
  instances.forEach(instance => {
    if (instance.tipEl && instance.el !== e.target && !instance.el.contains(e.target)) {
      instance.hide()
    }
  })
})

// Hide popovers when clicking outside (for click trigger)
document.addEventListener('click', e => {
  instances.forEach(instance => {
    if (instance.visible && instance.triggers.includes('click')) {
      // Check if click is outside both trigger and popover
      const clickedTrigger = instance.el === e.target || instance.el.contains(e.target)
      const clickedPopover = instance.tipEl && (instance.tipEl === e.target || instance.tipEl.contains(e.target))
      if (!clickedTrigger && !clickedPopover) {
        instance.hide()
      }
    }
  })
})

// Hide popovers on scroll (they can get mispositioned)
window.addEventListener('scroll', () => {
  instances.forEach(instance => {
    if (instance.visible) {
      instance.hide()
    }
  })
}, true)

class Popover {
  constructor(el, binding) {
    this.el = el
    this.tipEl = null
    this.visible = false
    this.showTimeout = null
    this.hideTimeout = null
    this.uid = uid++

    this.update(binding)
    this.bindEvents()
  }

  update(binding) {
    // Support both string value and object { title, content }
    const { value } = binding
    if (typeof value === 'object' && value !== null) {
      this.title = value.title || ''
      this.content = value.content || ''
    } else {
      this.title = ''
      this.content = value || ''
    }

    this.placement = binding.arg || 'right' // Bootstrap default for popover is right
    this.html = !!binding.modifiers.html

    // Parse triggers from modifiers
    this.triggers = []
    if (binding.modifiers.hover) this.triggers.push('hover')
    if (binding.modifiers.focus) this.triggers.push('focus')
    if (binding.modifiers.click) this.triggers.push('click')
    // Default to click if no triggers specified (Bootstrap popover default)
    if (this.triggers.length === 0) this.triggers = ['click']

    // Update content if popover is visible
    if (this.tipEl) {
      this.updateTipContent()
    }
  }

  updateTipContent() {
    const titleEl = this.tipEl.querySelector('.popover-title')
    const contentEl = this.tipEl.querySelector('.popover-content')

    if (titleEl) {
      if (this.title) {
        if (this.html) {
          titleEl.innerHTML = this.title
        } else {
          titleEl.textContent = this.title
        }
        titleEl.style.display = ''
      } else {
        titleEl.style.display = 'none'
      }
    }

    if (contentEl) {
      if (this.html) {
        contentEl.innerHTML = this.content
      } else {
        contentEl.textContent = this.content
      }
    }
  }

  bindEvents() {
    this.handlers = {
      mouseenter: () => this.scheduleShow(),
      mouseleave: () => this.scheduleHide(),
      focusin: () => this.scheduleShow(),
      focusout: () => this.scheduleHide(),
      click: e => {
        e.preventDefault()
        e.stopPropagation()
        this.toggle()
      }
    }

    if (this.triggers.includes('hover')) {
      this.el.addEventListener('mouseenter', this.handlers.mouseenter)
      this.el.addEventListener('mouseleave', this.handlers.mouseleave)
    }
    if (this.triggers.includes('focus')) {
      this.el.addEventListener('focusin', this.handlers.focusin)
      this.el.addEventListener('focusout', this.handlers.focusout)
    }
    if (this.triggers.includes('click')) {
      this.el.addEventListener('click', this.handlers.click)
    }
  }

  unbindEvents() {
    this.el.removeEventListener('mouseenter', this.handlers.mouseenter)
    this.el.removeEventListener('mouseleave', this.handlers.mouseleave)
    this.el.removeEventListener('focusin', this.handlers.focusin)
    this.el.removeEventListener('focusout', this.handlers.focusout)
    this.el.removeEventListener('click', this.handlers.click)
  }

  createTipEl() {
    const tip = document.createElement('div')
    tip.className = 'popover'
    tip.setAttribute('role', 'tooltip')
    tip.id = `popover-${this.uid}`

    const arrow = document.createElement('div')
    arrow.className = 'arrow'

    // Title element (hidden by default, Bootstrap hides empty titles)
    const titleEl = document.createElement('h3')
    titleEl.className = 'popover-title'
    titleEl.style.display = 'none'

    const contentEl = document.createElement('div')
    contentEl.className = 'popover-content'

    tip.appendChild(arrow)
    tip.appendChild(titleEl)
    tip.appendChild(contentEl)

    this.tipEl = tip
    this.updateTipContent()

    return tip
  }

  scheduleShow() {
    clearTimeout(this.hideTimeout)
    this.showTimeout = setTimeout(() => this.show(), 0)
  }

  scheduleHide() {
    clearTimeout(this.showTimeout)
    this.hideTimeout = setTimeout(() => this.hide(), 0)
  }

  show() {
    if (!this.content) return
    if (this.visible) return

    // Create popover element if needed
    if (!this.tipEl) {
      this.tipEl = this.createTipEl()
    }

    // Add to body
    document.body.appendChild(this.tipEl)

    // Position it
    const pos = PopoverPosition.position(this.el, this.tipEl, this.placement)

    // Remove old placement classes, add new one
    this.tipEl.className = `popover ${pos.placement}`

    // Apply position
    this.tipEl.style.top = `${pos.top}px`
    this.tipEl.style.left = `${pos.left}px`
    this.tipEl.style.display = 'block'

    // Adjust arrow if needed
    if (pos.arrowOffset !== null) {
      const arrow = this.tipEl.querySelector('.arrow')
      if (pos.placement === 'top' || pos.placement === 'bottom') {
        arrow.style.left = `${pos.arrowOffset}%`
      } else {
        arrow.style.top = `${pos.arrowOffset}%`
      }
    }

    // Trigger reflow then add 'in' class for animation
    this.tipEl.offsetHeight // force reflow
    this.tipEl.classList.add('in')

    this.visible = true
    this.el.setAttribute('aria-describedby', this.tipEl.id)
  }

  hide() {
    if (!this.visible) return
    if (!this.tipEl) return

    this.tipEl.classList.remove('in')

    // Remove after transition
    setTimeout(() => {
      this.tipEl?.parentNode?.removeChild(this.tipEl)
    }, 150) // Match Bootstrap's TRANSITION_DURATION

    this.visible = false
    this.el.removeAttribute('aria-describedby')
  }

  toggle() {
    if (this.visible) {
      this.hide()
    } else {
      this.show()
    }
  }

  destroy() {
    clearTimeout(this.showTimeout)
    clearTimeout(this.hideTimeout)
    this.unbindEvents()
    this.tipEl?.parentNode?.removeChild(this.tipEl)
    this.tipEl = null
  }
}

// Vue 3 directive (exported for app.directive() registration)
export default {
  beforeMount(el, binding) {
    const instance = new Popover(el, binding)
    el._popoverInstance = instance
    instances.push(instance)
  },
  updated(el, binding) {
    el._popoverInstance?.update(binding)
  },
  unmounted(el) {
    if (el._popoverInstance) {
      const idx = instances.indexOf(el._popoverInstance)
      if (idx !== -1) instances.splice(idx, 1)
      el._popoverInstance.destroy()
      delete el._popoverInstance
    }
  }
}
